import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import SlotMachine from './components/SlotMachine';
import GameResult from './components/GameResult';
import ControllersBackground from './components/ControllersBackground';
import Terms from './pages/Terms';
import { retroGames, platformFilters, systems as defaultSystems, genres as defaultGenres, vibes as defaultVibes } from './mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIBES = ['Essential', 'Hidden Gem', 'Arcade Classic', 'Cult Classic', 'Deep Cut', 'One-More-Go'];
const getRandomVibe = () => VIBES[Math.floor(Math.random() * VIBES.length)];

function HomePage() {
  const [selectedFilters, setSelectedFilters] = useState(['all']);
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentGame, setCurrentGame] = useState(null);
  const [spinCount, setSpinCount] = useState(1501);
  const [lockedSlots, setLockedSlots] = useState({
    system: false,
    genre: false,
    vibe: false
  });
  const [hasSpun, setHasSpun] = useState(false);
  const [selectedValues, setSelectedValues] = useState({
    system: null,
    genre: null,
    vibe: null
  });
  // Wheel arrays kept in state so we can inject IGDB-returned values that
  // aren't in the static seed lists. Guarantees the locked center text
  // always matches the actual returned game's system / genre / vibe.
  const [systemsList, setSystemsList] = useState(defaultSystems);
  const [genresList, setGenresList] = useState(defaultGenres);
  const [vibesList, setVibesList] = useState(defaultVibes);

  const injectIntoWheels = useCallback((game) => {
    if (!game) return;
    if (game.system) {
      setSystemsList(prev => prev.includes(game.system) ? prev : [game.system, ...prev]);
    }
    if (game.genre) {
      setGenresList(prev => prev.includes(game.genre) ? prev : [game.genre, ...prev]);
    }
    if (game.vibe) {
      setVibesList(prev => prev.includes(game.vibe) ? prev : [game.vibe, ...prev]);
    }
  }, []);

  // Increment the global spin counter; falls back to local +1 on failure.
  const incrementSpinCount = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/spin-count/increment`);
      setSpinCount(res.data.count);
    } catch (err) {
      console.error('Error updating spin count:', err);
      setSpinCount(prev => prev + 1);
    }
  }, []);

  // Fetch global spin count on component mount
  useEffect(() => {
    const fetchSpinCount = async () => {
      try {
        const response = await axios.get(`${API}/spin-count`);
        setSpinCount(response.data.count);
      } catch (error) {
        console.error('Error fetching spin count:', error);
      }
    };
    fetchSpinCount();
  }, []);

  // Deep-link handler: if URL has ?game=<id>, fetch and display that game
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gameId = params.get('game');
    if (!gameId) return;

    const loadSharedGame = async () => {
      try {
        const response = await axios.get(`${API}/games/by-id/${gameId}`);
        const sharedGame = { ...response.data, vibe: getRandomVibe() };
        injectIntoWheels(sharedGame);
        setCurrentGame(sharedGame);
        setHasSpun(true);
        setSelectedValues({
          system: sharedGame.system,
          genre: sharedGame.genre,
          vibe: sharedGame.vibe
        });
      } catch (error) {
        console.error('Error loading shared game:', error);
      }
    };
    loadSharedGame();
  }, [injectIntoWheels]);

  const toggleFilter = (filterId) => {
    if (filterId === 'all') {
      if (selectedFilters.includes('all')) {
        setSelectedFilters([]);
      } else {
        setSelectedFilters(['all']);
      }
    } else {
      setSelectedFilters(prev => {
        const withoutAll = prev.filter(f => f !== 'all');
        if (withoutAll.includes(filterId)) {
          const newFilters = withoutAll.filter(f => f !== filterId);
          return newFilters.length === 0 ? ['all'] : newFilters;
        } else {
          return [...withoutAll, filterId];
        }
      });
    }
  };

  const applySpunGame = (game) => {
    injectIntoWheels(game);
    setCurrentGame(game);
    setSelectedValues(prev => ({
      system: lockedSlots.system ? prev.system : game.system,
      genre: lockedSlots.genre ? prev.genre : game.genre,
      vibe: lockedSlots.vibe ? prev.vibe : game.vibe
    }));
  };

  const handleSpin = async () => {
    setIsSpinning(true);
    setHasSpun(true);

    let apiUrl = `${API}/games/random`;
    const params = new URLSearchParams();
    if (!selectedFilters.includes('all')) {
      selectedFilters.forEach(f => params.append('platform_category', f));
    }
    // Locked wheels constrain the IGDB query so spins respect the user's pick.
    // (vibe is client-side only — no backend filter)
    if (lockedSlots.system && selectedValues.system) {
      params.append('system', selectedValues.system);
    }
    if (lockedSlots.genre && selectedValues.genre) {
      params.append('genre', selectedValues.genre);
    }
    if (params.toString()) {
      apiUrl += `?${params.toString()}`;
    }

    try {
      const response = await axios.get(apiUrl);
      applySpunGame({ ...response.data, vibe: getRandomVibe() });
    } catch (error) {
      console.error('Error fetching game:', error);
      // Fallback to mock data if API fails
      const randomGame = retroGames[Math.floor(Math.random() * retroGames.length)];
      applySpunGame(randomGame);
    }

    await incrementSpinCount();
    setIsSpinning(false);
  };

  const handleReset = () => {
    setLockedSlots({
      system: false,
      genre: false,
      vibe: false
    });
    setCurrentGame(null);
    setHasSpun(false);
    setSelectedValues({
      system: null,
      genre: null,
      vibe: null
    });
  };

  const toggleLock = (type) => {
    if (!isSpinning) {
      setLockedSlots(prev => ({
        ...prev,
        [type]: !prev[type]
      }));
    }
  };

  // Nudge handler — user tapped a non-center value on a wheel; update
  // selectedValues so the lock and result-card reflect the chosen value.
  const handleNudge = (type, value) => {
    setSelectedValues(prev => ({ ...prev, [type]: value }));
  };

  return (
    <div className="App min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <ControllersBackground />
      
      {/* Header */}
      <header className="relative pt-6 pb-4 px-4">
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black text-center tracking-tight mb-6 sm:mb-8 px-2">
          <span className="bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">BACKLOG</span>
          {' '}
          <span className="text-white">KILLER</span>
        </h1>

        {/* Platform Filters */}
        <div className="flex justify-center gap-2 sm:gap-3 flex-wrap px-2 sm:px-4" data-testid="platform-filters">
          {platformFilters.map(filter => (
            <button
              key={filter.id}
              data-testid={`platform-filter-${filter.id}`}
              onClick={() => toggleFilter(filter.id)}
              className={`px-3 py-1.5 sm:px-6 sm:py-2.5 rounded border-2 text-xs sm:text-base font-black tracking-wider transition-all duration-300 ${
                selectedFilters.includes(filter.id)
                  ? 'bg-yellow-600 border-yellow-600 text-gray-950'
                  : 'bg-transparent border-gray-700 text-gray-400 hover:border-gray-500'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="mb-12">
          <SlotMachine 
            isSpinning={isSpinning}
            lockedSlots={lockedSlots}
            onToggleLock={toggleLock}
            onNudge={handleNudge}
            selectedValues={selectedValues}
            systems={systemsList}
            genres={genresList}
            vibes={vibesList}
          />
        </div>

        {/* Spin Controls */}
        <div className="text-center mb-8 px-4">
          {!hasSpun && (
            <p className="text-base sm:text-lg font-bold text-gray-500 mb-4">hit <span className="italic">spin</span> for a random pick</p>
          )}
          {hasSpun && currentGame && (
            <p className="text-base sm:text-lg font-bold text-gray-500 mb-4">tap the values above or below to nudge</p>
          )}
          
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 items-center">
            <button
              onClick={handleSpin}
              disabled={isSpinning}
              data-testid="spin-button"
              className={`w-full sm:w-auto px-12 sm:px-20 py-4 sm:py-5 rounded-lg font-black text-xl sm:text-2xl tracking-wide transition-all duration-300 transform ${
                isSpinning
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-gray-950 hover:scale-105 shadow-lg shadow-yellow-900/50'
              }`}
            >
              {isSpinning ? 'SPINNING...' : hasSpun ? 'SPIN AGAIN' : 'SPIN'}
            </button>
            {hasSpun && (
              <button
                onClick={handleReset}
                data-testid="reset-button"
                className="w-full sm:w-auto px-6 sm:px-8 py-4 sm:py-5 rounded-lg font-black text-sm sm:text-base border-2 border-gray-700 hover:border-gray-500 text-gray-400 hover:text-gray-300 transition-all"
              >
                reset all
              </button>
            )}
          </div>
        </div>

        {/* Game Result */}
        {currentGame && !isSpinning && (
          <GameResult game={currentGame} onSpinAgain={handleSpin} />
        )}

        {/* Insert Coin Section */}
        {!hasSpun && (
          <div className="text-center mt-20 mb-12">
            <h2 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
              INSERT COIN
            </h2>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 sm:py-8 text-gray-600 text-sm sm:text-base font-bold border-t border-gray-800 px-4">
        <p className="mb-2" data-testid="spin-counter">{spinCount.toLocaleString()} spins and counting</p>
        <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 text-xs sm:text-sm">
          <a href="https://twitter.com/backlogkiller" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400 transition-colors">@backlogkiller</a>
          <span className="hidden sm:inline">·</span>
          <a href="/terms" className="hover:text-gray-400 transition-colors">terms</a>
          <span className="hidden sm:inline">·</span>
          <span>This site operates affiliate links.</span>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/terms" element={<Terms />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
